<template>
  <div class="virtual-sport-item-card">
    <div class="card-content">
      <!-- Image and Info -->
      <div class="left-section">
        <!-- Image -->
        <div class="activity-image">
          <img :src="item.evidenceImageUrl" class="activity-img" />
        </div>

        <!-- Info -->
        <div class="activity-info">
          <div class="project-row">
            <h3 class="address">{{ item.projectCode }}</h3>
            <h3 class="address">{{ item.addressNo }}</h3>
          </div>
          <p class="customer-name">{{ item.customerName }}</p>

          <div
            v-if="item.status === virtualSportApprovalStatus.APPROVED"
            class="score-stats"
          >
            <div class="score-display">
              <span class="score-number">{{ item.points }}</span>
              <img src="/static/icons/star.svg" alt="star" width="20" />
            </div>
            <div class="stats-info">
              <span>{{ item.calories }} Cal</span>
              <span>{{ formatDuration(item.workoutDurationSeconds) }}</span>
            </div>
          </div>

          <div v-else-if="item.status === virtualSportApprovalStatus.REJECTED">
            <span class="rejection-reason"
              >เหตุผล: {{ getRejectionReasonText() }}</span
            >
          </div>

          <p v-else class="booking-time">
            <span class="time-label">ส่งข้อมูลเมื่อ</span>
            {{ formatDateTime(item.createdAt) }}
          </p>
        </div>
      </div>

      <!-- Action Button -->
      <div>
        <sc-button
          :variant="
            item.status === virtualSportApprovalStatus.PENDING
              ? 'primary'
              : 'secondary'
          "
          @click="handleClick"
        >
          {{ getButtonText() }}
        </sc-button>
      </div>
    </div>
  </div>
</template>

<script>
// 3rd Parties
import { DateTime } from "luxon"

// Components
import ScButton from "@/lib/components/ScButton"

// Enums
import {
  virtualSportApprovalStatus,
  virtualSportRejectionReason,
  virtualSportCaloriesPerStar,
} from "../config/enums"

export default {
  name: "VirtualSportListItem",
  components: {
    ScButton,
  },
  props: {
    item: {
      type: null,
      default: undefined,
    },
  },
  data() {
    return {
      virtualSportApprovalStatus,
      virtualSportRejectionReason,
      virtualSportCaloriesPerStar,
    }
  },
  methods: {
    handleClick() {
      this.$emit("onClick", this.item)
    },
    formatDateTime(timestamp) {
      if (!timestamp) return "-"

      const dateTime = DateTime.fromISO(timestamp)

      return dateTime.toFormat("dd/MM/yyyy , HH:mm:ss")
    },
    formatDuration(seconds) {
      if (!seconds || seconds <= 0) return "0m"

      const totalMinutes = Math.floor(seconds / 60)
      const hours = Math.floor(totalMinutes / 60)
      const remainingMinutes = totalMinutes % 60

      if (hours > 0) {
        return remainingMinutes > 0
          ? `${hours}h ${remainingMinutes}m`
          : `${hours}h`
      }

      return `${totalMinutes}m`
    },
    getButtonText() {
      switch (this.item.status) {
        case virtualSportApprovalStatus.PENDING:
          return "ประเมิน"
        case virtualSportApprovalStatus.APPROVED:
          return "แก้ไข"
        case virtualSportApprovalStatus.REJECTED:
          return "แก้ไข"
        default:
          return "ประเมิน"
      }
    },
    getRejectionReasonText() {
      const { rejectionReason } = this.item

      if (!rejectionReason) {
        return "ไม่ได้ระบุเหตุผล"
      }

      return (
        this.virtualSportRejectionReason[rejectionReason] || "ไม่ได้ระบุเหตุผล"
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.virtual-sport-item-card {
  background: white;
  border-radius: 12px;
  margin-bottom: 12px;
  border: 1px solid #f0f2f5;

  .card-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
  }

  .left-section {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
  }

  .activity-image {
    height: 80px;
    width: 80px;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;

    .activity-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 8px;
    }
  }

  .activity-info {
    .project-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .address {
      font-size: 16px;
      font-weight: bold;
      color: $orange;
      margin: 0;
    }

    .customer-name {
      font-size: 16px;
      font-weight: bold;
    }

    .booking-time {
      font-size: 14px;
      margin: 0;

      .time-label {
        margin-right: 4px;
      }
    }

    .score-stats {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-top: 2px;
    }

    .score-display {
      display: flex;
      align-items: center;
      gap: 6px;

      .score-number {
        font-size: 20px;
        font-weight: 700;
        line-height: 1;
      }
    }

    .stats-info {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 13px;
      font-weight: 500;
    }

    .rejection-reason {
      font-size: 14px;
    }
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .virtual-sport-item-card {
    .card-content {
      padding: 14px 16px;
      gap: 12px;
    }

    .activity-image {
      width: 56px;
      height: 56px;
    }

    .activity-info {
      .address {
        font-size: 15px;
      }

      .customer-name {
        font-size: 13px;
      }

      .booking-time {
        font-size: 12px;
      }
    }
  }
}

@media (max-width: 480px) {
  .virtual-sport-item-card {
    .card-content {
      flex-direction: column;
      align-items: stretch;
      gap: 16px;
    }

    .left-section {
      gap: 12px;
    }

    .activity-info {
      .score-stats {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
      }

      .stats-info {
        gap: 8px;
      }
    }
  }
}
</style>
